import { slugify } from '@/utils/slugify';
import { useCms } from '@/hooks/useCms';
import { useNavigate, useParams } from 'react-router-dom';
import { trackCustomEvent } from '@/lib/appInsights';
import { TrackingCustomEvents } from '@/constants/constants';
import { useIsAuthenticated } from '@azure/msal-react';
import FilterBar from '@/components/FilterBar';
import { useUser } from '@/state/UserContext';
import type { CMSContextType } from '@/types/util.types';

interface ArticleTypeFilterProps {
  className?: string;
  disabled?: boolean;
}

function ArticleTypeFilter({ className, disabled }: ArticleTypeFilterProps) {
  const params = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { email, userId } = useUser();
  const { contentLabels } = useCms() as CMSContextType;
  const typeSlug = params.contentLabel;
  const allOption = { id: 0, name: 'All' };
  const options = [allOption, ...contentLabels];
  const selectedOptionId =
    options.find((option) => typeSlug === slugify(option.name) || (option.id === 0 && !typeSlug))?.id || 0;

  function setTypeFilter(optionId: string | number) {
    const option = options.find((opt) => opt.id === optionId);
    if (!option) return;

    const typeName = option.name;
    const categorySlug = params.category;
    const subCategorySlug = params.subCategory || 'all';

    trackCustomEvent(TrackingCustomEvents.click.category.contentLabelFilter, {
      category: params.category ?? 'all',
      sub_category: params.subCategory ?? 'all',
      content_label: params.contentLabel ?? 'all',
      user_auth_status: isAuthenticated ? 'logged_in' : 'anonymous',
      user_email: email ?? 'anonymous',
      user_id: userId ?? 'anonymous',
    });

    if (typeName.toLowerCase() === 'all') {
      return navigate(`/category/${categorySlug}/${subCategorySlug}`);
    } else {
      const newType = slugify(typeName);
      return navigate(`/category/${categorySlug}/${subCategorySlug}/${newType}`);
    }
  }

  return (
    <FilterBar
      label='Type'
      options={options}
      selectedOptionId={selectedOptionId}
      onOptionClick={setTypeFilter}
      disabled={disabled}
      className={className}
      dataCy='article-type-filter'
    />
  );
}

export default ArticleTypeFilter;
